import {
  exampleJpegUrl,
  EXAMPLES,
  outdoorImage,
  outdoorPhotoUrl,
  roomImage,
} from './assets';
import {
  findInteriorRoom,
  INTERIOR_ROOMS,
  type InteriorRoom,
  toolRoomType,
} from './rooms';
import type {
  InteriorLandingPage,
  LandingStep,
  SamplePhoto,
  ShowcaseScene,
} from './types';

/** Paid plans on sale, cheapest first (their prices come from the database). */
export const PAID_PLANS = ['Starter', 'Unlimited 50', 'Unlimited'] as const;
export type PlanPrices = Record<(typeof PAID_PLANS)[number], number>;

export const HUB_PATH = '/ai-interior-design-generator';
export const STAGING_PATH = '/ai-virtual-staging';
export const PAINT_PATH = '/paint-color-visualizer';
export const LANDSCAPE_PATH = '/ai-landscape-design';
export const EXTERIOR_PATH = '/ai-exterior-design';

/**
 * Outdoor rooms whose room-types photos were shot indoors (the "house
 * exterior" pair is a living room). Their before/after pairs were made for
 * these pages instead: see OUTDOOR_PAIRS in the upload script.
 */
export const OUTDOOR_PAIR_ROOMS = new Set([
  'house-exterior',
  'front-yard',
  'backyard',
  'outdoor-pool-area',
]);

/** Rooms with their own page elsewhere (the room URL redirects there). */
export const ROOMS_WITH_OWN_PAGE: Record<string, string> = {
  'house-exterior': EXTERIOR_PATH,
};

export const roomPath = (slug: string) =>
  ROOMS_WITH_OWN_PAGE[slug] ?? `${HUB_PATH}/${slug}`;

const photoOf = (room: InteriorRoom) =>
  OUTDOOR_PAIR_ROOMS.has(room.slug) ? outdoorImage : roomImage;

/** The three redesigns every room photo has, lightest first. */
export const roomScene = (room: InteriorRoom): ShowcaseScene => {
  const image = photoOf(room);
  return {
    label: room.name,
    before: image(room.slug, 'before'),
    looks: [
      { label: 'Modern', image: image(room.slug, 'after-1') },
      { label: 'Natural', image: image(room.slug, 'after-2') },
      { label: 'Luxe', image: image(room.slug, 'after-3') },
    ],
    initialLook: 2,
  };
};

/** The original room photos (JPEG), which the designer's samples use too. */
const roomPhotoUrl = (slug: string) =>
  `https://media.easy-peasy.ai/room-types/${slug}-before.jpeg`;

export const roomSample = (room: InteriorRoom): SamplePhoto => ({
  label: room.name,
  image: photoOf(room)(room.slug, 'before'),
  source: OUTDOOR_PAIR_ROOMS.has(room.slug)
    ? outdoorPhotoUrl(room.slug)
    : roomPhotoUrl(room.slug),
  roomType: toolRoomType(room),
});

const room = (slug: string) => {
  const found = findInteriorRoom(slug);
  if (!found) throw new Error(`Unknown interior room: ${slug}`);
  return found;
};

const DESIGN_STEPS: [LandingStep, LandingStep, LandingStep] = [
  {
    title: 'Upload a photo',
    body: 'Take one with your phone or use a photo you already have. A wide shot from a corner works best.',
  },
  {
    title: 'Pick a style',
    body: 'Choose from {{styles}} styles, from Scandinavian to Art Deco, or describe the room you want.',
  },
  {
    title: 'Refine and download',
    body: 'Compare the designs, change any detail with a few words and download the one you love.',
  },
];

const ROOM_POINTS: [string, string, string] = [
  'Keeps your walls, windows and layout',
  '{{styles}} styles to try',
  'Change any detail in your own words',
];

export const HUB_PAGE: InteriorLandingPage = {
  id: 'hub',
  path: HUB_PATH,
  kind: 'hub',
  seoTitle: 'AI Interior Design: Redesign Any Room from a Photo, Free',
  seoDescription:
    'Upload a photo and redesign any room with AI: restyle it in {{styles}} styles, stage empty rooms, clear clutter, try wall colors or start from scratch. Free to try.',
  ogTitle: 'AI Interior Design',
  eyebrow: 'Redesign any room from a photo',
  title: 'AI Interior Design',
  titleTail: 'for the room you actually have.',
  lead: 'Upload a photo and see your room in a new style, with the same walls, windows and layout. Then change any detail in your own words, furnish an empty room or try a new wall color.',
  points: ROOM_POINTS,
  mode: 'restyle',
  uploadLabel: 'Upload a photo of your room',
  scratch: 'Describe the room and design it from scratch.',
  samples: [
    roomSample(room('kitchen')),
    roomSample(room('living-room')),
    roomSample(room('bedroom')),
  ],
  // The kitchen's change shows at a glance (its cabinets fill the right half).
  scenes: [
    roomScene(room('kitchen')),
    roomScene(room('living-room')),
    roomScene(room('bedroom')),
    roomScene(room('bathroom')),
  ],
  steps: DESIGN_STEPS,
  faqs: [
    {
      question: 'Can AI design my room from a photo?',
      answer:
        'Yes. Upload a photo of any room and pick a style, and the AI redesigns it while keeping your walls, windows and layout. Then you can change any detail by describing it.',
    },
    {
      question: 'What can I design besides rooms in my home?',
      answer:
        'Gardens, backyards, patios and house exteriors, and business spaces such as cafés, salons, offices, shops and classrooms. There are {{rooms}} room types to choose from.',
    },
  ],
  breadcrumb: [{ name: 'AI Interior Design', path: HUB_PATH }],
  finalTitle: 'See your room in a new style today.',
};

export const ROOM_STEPS: [LandingStep, LandingStep, LandingStep] = [
  {
    title: 'Take a photo of your {{room_lower}}',
    body: 'A phone photo is fine. Stand in a corner or doorway and fit in as much of the room as you can.',
  },
  {
    title: 'Pick a style or an idea',
    body: 'Choose from {{styles}} styles, start from one of the ideas on this page, or describe what you want.',
  },
  {
    title: 'Refine and download',
    body: 'Compare the designs, change any detail with a few words and download the one you love.',
  },
];

/** Outdoor spaces have no walls and windows to keep, and aren't rooms. */
const OUTDOOR_ROOM_POINTS: [string, string, string] = [
  'Keeps the layout and everything around it',
  ROOM_POINTS[1],
  ROOM_POINTS[2],
];

const OUTDOOR_ROOM_STEPS: [LandingStep, LandingStep, LandingStep] = [
  {
    ...ROOM_STEPS[0],
    body: 'Stand where you get the best view and fit in the whole space. Daylight works best.',
  },
  ROOM_STEPS[1],
  ROOM_STEPS[2],
];

/** Same-group rooms offered as samples next to the page's own photo. */
const sampleRoomsFor = (current: InteriorRoom) => [
  current,
  ...INTERIOR_ROOMS.filter(
    (other) => other.group === current.group && other.slug !== current.slug,
  ).slice(0, 2),
];

export function buildRoomPage(current: InteriorRoom): InteriorLandingPage {
  const outdoor = current.group === 'outdoor';
  return {
    id: `room:${current.slug}`,
    path: `${HUB_PATH}/${current.slug}`,
    kind: 'room',
    seoTitle: current.remodel
      ? 'AI {{room}} Design & Remodel Ideas from Your Photo'
      : 'AI {{room}} Design: Free {{room}} Ideas from Your Photo',
    seoTitleShort: 'AI {{room}} Design: Free Ideas from Your Photo',
    seoDescription:
      'Upload a photo of your {{room_lower}} and see it redesigned in a new style. Try {{styles}} styles, swap furniture, change colors or start from scratch.',
    ogTitle: 'AI {{room}} Design',
    eyebrow: '{{room}} ideas from your own photo',
    title: 'AI {{room}} Design',
    // Not the room's name again: the title already has it.
    titleTail: 'from your own photo.',
    lead: outdoor
      ? 'See your {{room_lower}} in a new style, with the same layout and surroundings. Then change any detail in your own words, or design one from scratch.'
      : 'See your {{room_lower}} in a new style, with the same walls, windows and layout. Then change any detail in your own words, or design one from scratch.',
    points: outdoor ? OUTDOOR_ROOM_POINTS : ROOM_POINTS,
    mode: 'restyle',
    roomType: toolRoomType(current),
    uploadLabel: 'Upload a photo of your {{room_lower}}',
    scratch: outdoor
      ? 'Describe the space and design it from scratch.'
      : 'Describe the room and design it from scratch.',
    samples: sampleRoomsFor(current).map(roomSample),
    scenes: [roomScene(current)],
    steps: outdoor ? OUTDOOR_ROOM_STEPS : ROOM_STEPS,
    faqs: [],
    breadcrumb: [
      { name: 'AI Interior Design', path: HUB_PATH },
      { name: 'AI {{room}} Design', path: `${HUB_PATH}/${current.slug}` },
    ],
    finalTitle: 'Your {{room_lower}}, redesigned.',
    vars: { room: current.name },
  };
}

export const STAGING_PAGE: InteriorLandingPage = {
  id: 'virtual-staging',
  path: STAGING_PATH,
  kind: 'tool',
  seoTitle: 'AI Virtual Staging: Furnish Empty Rooms from a Photo',
  seoDescription:
    'Virtually stage empty rooms with AI. Upload a listing photo, describe the look and get a furnished room that keeps the real walls, windows and floors. Free to try.',
  ogTitle: 'AI Virtual Staging',
  eyebrow: 'Virtual staging for listings and plans',
  title: 'AI Virtual Staging',
  titleTail: 'that keeps the room real.',
  lead: 'Upload a photo of an empty room and get it furnished in the style you choose. The walls, windows and floors stay as they are, so buyers see the space they would walk into.',
  points: [
    'Keeps walls, windows and floors',
    'Empty room clears old furniture first',
    'High-resolution downloads',
  ],
  mode: 'virtual-staging',
  uploadLabel: 'Upload a photo of an empty room',
  samples: [
    {
      label: 'Empty living room',
      image: EXAMPLES.stagingEmpty,
      source: exampleJpegUrl('staging-empty', 1600),
      roomType: 'Living Room',
    },
  ],
  scenes: [
    {
      label: 'Living Room',
      before: EXAMPLES.stagingEmpty,
      looks: [{ label: 'Scandinavian', image: EXAMPLES.stagingStaged }],
      // The staged sofa is on the left: start with most of it in view.
      split: 28,
    },
  ],
  steps: [
    {
      title: 'Upload the listing photo',
      body: 'Use the photo you will list. If the room still has furniture in it, clear it first with Empty room.',
    },
    {
      title: 'Choose a look',
      body: 'Describe the furniture you want, like modern, Scandinavian, mid-century or a luxury hotel feel.',
    },
    {
      title: 'Download and list',
      body: 'Download the staged photo in high resolution. Paid plans can also make 4K images.',
    },
  ],
  faqs: [
    {
      question: 'How much does virtual staging cost?',
      answer:
        'Each staged photo uses {{credits}} credits. Every account can make its first design free, and the Starter plan includes enough credits for about {{starter}} staged photos a month.',
    },
    {
      question: 'Do I need an empty room?',
      answer:
        'No. If the photo has furniture in it, use Empty room to clear it first, then stage the empty space in any style.',
    },
    {
      question: 'Will it change the walls, windows or floors?',
      answer:
        'No. Virtual staging adds furniture and decor and keeps the architecture of the room. If you want new floors or wall colors too, describe them in Edit.',
    },
    {
      question: 'Do I have to label virtually staged photos?',
      answer:
        'Many listing services and real estate boards require it. Mark the photos as virtually staged, and keep the original photo in the listing where your local rules ask for it.',
    },
  ],
  breadcrumb: [
    { name: 'AI Interior Design', path: HUB_PATH },
    { name: 'AI Virtual Staging', path: STAGING_PATH },
  ],
  finalTitle: 'Stage your next listing today.',
};

export const PAINT_PAGE: InteriorLandingPage = {
  id: 'paint-visualizer',
  path: PAINT_PATH,
  kind: 'tool',
  seoTitle: 'Paint Color Visualizer: Try Wall Colors on Your Photo (Free)',
  seoDescription:
    'See paint colors on your own walls before you buy. Upload a photo of your room, pick a color and get a realistic preview that keeps everything else the same.',
  ogTitle: 'Paint Color Visualizer',
  eyebrow: 'Try wall colors on your own room',
  title: 'Paint Color Visualizer',
  titleTail: 'for your own walls.',
  lead: 'Upload a photo of your room and pick a color. Only the walls change: the furniture, floors and light stay as they are, so you see the real effect before you buy paint.',
  points: [
    'Only the walls change',
    'Any color you can name',
    'Compare before and after',
  ],
  mode: 'color-changer',
  uploadLabel: 'Upload a photo of your room',
  paint: true,
  samples: [
    {
      label: 'Living Room',
      image: EXAMPLES.livingBefore,
      source: roomPhotoUrl('living-room'),
      roomType: 'Living Room',
    },
    {
      label: 'Bathroom',
      image: EXAMPLES.bathroomBefore,
      source: roomPhotoUrl('bathroom'),
      roomType: 'Bathroom',
    },
  ],
  scenes: [
    {
      label: 'Bathroom',
      before: EXAMPLES.bathroomBefore,
      looks: [
        {
          label: 'Terracotta',
          image: EXAMPLES.paintTerracotta,
          swatch: '#C46E4F',
        },
      ],
    },
    {
      label: 'Living Room',
      before: EXAMPLES.livingBefore,
      looks: [
        { label: 'Sage green', image: EXAMPLES.paintSage, swatch: '#A9B79A' },
      ],
    },
  ],
  steps: [
    {
      title: 'Upload a photo',
      body: 'A daytime photo with the walls in view works best. Phone photos are fine.',
    },
    {
      title: 'Pick a color',
      body: 'Choose one of the ready colors or type any color you like, from a soft greige to a deep navy.',
    },
    {
      title: 'Compare and decide',
      body: 'Drag between before and after, try more colors and download the one you like best.',
    },
  ],
  faqs: [
    {
      question: 'Will it change my furniture or floors?',
      answer:
        'No. Paint walls changes only the wall color and keeps the furniture, floors, ceiling, windows and doors as they are.',
    },
    {
      question: 'Can I use a paint brand’s color name?',
      answer:
        'Yes. Type the color’s name and add a short description, like “Hague Blue, a deep navy”. Treat the result as a guide and check a real sample on your wall before you buy.',
    },
    {
      question: 'Can I paint kitchen cabinets or the outside of my house?',
      answer:
        'Yes, with Edit: upload the photo and write what to change, such as “paint the cabinets sage green” or “paint the house white with black trim”.',
    },
    {
      question: 'How accurate are the colors?',
      answer:
        'Close, but screens and daylight change how paint looks. Use the preview to narrow down your choice, then try a sample pot on the wall.',
    },
  ],
  breadcrumb: [
    { name: 'AI Interior Design', path: HUB_PATH },
    { name: 'Paint Color Visualizer', path: PAINT_PATH },
  ],
  finalTitle: 'Find your wall color before you paint.',
};

export const LANDSCAPE_PAGE: InteriorLandingPage = {
  id: 'landscape',
  path: LANDSCAPE_PATH,
  kind: 'tool',
  seoTitle: 'AI Landscape Design: Free Yard & Garden Ideas from a Photo',
  seoDescription:
    'Redesign your yard with AI. Upload a photo of your backyard, front yard or garden, pick a style and see new plants, paths, patios and lighting. Free to try.',
  ogTitle: 'AI Landscape Design',
  eyebrow: 'Backyards, front yards and gardens',
  title: 'AI Landscape Design',
  titleTail: 'from a photo of your yard.',
  lead: 'Upload a photo of your backyard, front yard or garden and see it with new plants, paths, seating and lighting. Change any detail in your own words, or describe the yard you want.',
  points: [
    'Keeps your house, fences and the shape of the yard',
    'Add a deck, patio or pool with words',
    'Ideas for every outdoor space',
  ],
  mode: 'restyle',
  roomType: 'Backyard',
  uploadLabel: 'Upload a photo of your yard or garden',
  scratch: 'Describe the yard you want and design it from scratch.',
  samples: [
    roomSample(room('backyard')),
    roomSample(room('front-yard')),
    roomSample(room('outdoor-pool-area')),
  ],
  scenes: [
    roomScene(room('backyard')),
    roomScene(room('front-yard')),
    roomScene(room('outdoor-pool-area')),
  ],
  steps: [
    {
      title: 'Photograph your yard',
      body: 'Stand where you get the best view and fit in the whole space. Daylight works best.',
    },
    {
      title: 'Choose a style',
      body: 'Pick a style like Mediterranean, Japanese Zen or Tropical, or describe the plants and features you want.',
    },
    {
      title: 'Refine and download',
      body: 'Add a fire pit, swap the lawn for gravel or add lighting with a few words, then download your design.',
    },
  ],
  faqs: [
    {
      question: 'Can AI design my backyard from a photo?',
      answer:
        'Yes. Upload a photo of your backyard and pick a style or describe what you want. The AI keeps your house, fences and the shape of the yard and redesigns the planting, paths and seating.',
    },
    {
      question: 'Can I plan a garden without a photo?',
      answer:
        'Yes. Choose Create new and describe the garden you want, like “a small cottage garden with a stone path and roses”.',
    },
    {
      question: 'Will the plants suit my climate?',
      answer:
        'The AI draws what you describe, not a planting plan. Use the design for ideas and ask a local nursery or landscaper which plants grow well where you live.',
    },
    {
      question: 'Can I add a deck, patio or pool?',
      answer:
        'Yes. Describe it in Edit, such as “add a wooden deck with a dining table” or “add a small pool with a stone border”, and the AI adds it to your photo.',
    },
  ],
  breadcrumb: [{ name: 'AI Landscape Design', path: LANDSCAPE_PATH }],
  finalTitle: 'See your yard in a new light.',
};

export const EXTERIOR_PAGE: InteriorLandingPage = {
  id: 'exterior',
  path: EXTERIOR_PATH,
  kind: 'tool',
  seoTitle: 'AI Exterior Design: See Your House in a New Style (Free)',
  seoDescription:
    'Redesign your house exterior with AI. Upload a photo and try new siding, paint colors, roofs, windows and landscaping before you renovate. Free to try.',
  ogTitle: 'AI Exterior Design',
  eyebrow: 'Facades, colors and curb appeal',
  title: 'AI Exterior Design',
  titleTail: 'for your own house.',
  lead: 'Upload a photo of your house and see it in a new style. Try new siding, paint colors, a different roof or a new front yard, and keep the shape of the house you have.',
  points: [
    'Keeps the shape of your house',
    'Try siding, colors and roofs',
    'Front yard and garden too',
  ],
  mode: 'restyle',
  roomType: 'House Exterior',
  uploadLabel: 'Upload a photo of your house',
  scratch: 'Describe the house you want and design it from scratch.',
  samples: [roomSample(room('house-exterior')), roomSample(room('front-yard'))],
  scenes: [roomScene(room('house-exterior')), roomScene(room('front-yard'))],
  steps: [
    {
      title: 'Photograph the front of your house',
      body: 'Stand across the street or at the end of the driveway so the whole house fits in the frame.',
    },
    {
      title: 'Choose a style',
      body: 'Pick a style such as Modern, Mediterranean or Farmhouse, or describe the look you want.',
    },
    {
      title: 'Try the details',
      body: 'Change the siding, trim, front door or roof with a few words and download the version you like.',
    },
  ],
  faqs: [
    {
      question: 'Can I see new exterior paint colors on my house?',
      answer:
        'Yes. Upload a photo and use Edit with a request like “paint the siding white and the trim black”. For walls inside, the Paint Color Visualizer does it in one click.',
    },
    {
      question: 'Will it keep the shape of my house?',
      answer:
        'Yes. A restyle keeps the walls, roofline and windows where they are and changes the materials, colors and details. Describe bigger changes, like a new porch, in your own words.',
    },
    {
      question: 'Can I show the designs to a contractor?',
      answer:
        'Yes. Many people use them to show a builder or designer what they have in mind. They are pictures of an idea, not construction drawings.',
    },
  ],
  breadcrumb: [{ name: 'AI Exterior Design', path: EXTERIOR_PATH }],
  finalTitle: 'See your house in a new style today.',
};

/** Pages for one job, linked from every interior page. */
export const TOOL_PAGES = [
  {
    path: STAGING_PATH,
    label: 'AI Virtual Staging',
    image: EXAMPLES.stagingStaged,
  },
  {
    path: PAINT_PATH,
    label: 'Paint Color Visualizer',
    image: EXAMPLES.paintSage,
  },
  {
    path: LANDSCAPE_PATH,
    label: 'AI Landscape Design',
    image: outdoorImage('backyard', 'after-1'),
  },
  {
    path: EXTERIOR_PATH,
    label: 'AI Exterior Design',
    image: outdoorImage('house-exterior', 'after-2'),
  },
];
